import {
  CodingActivityCard,
  DataStatusNote,
  formatClassSection,
  formatDuration,
  formatPercent,
  GIL,
  HeaderButton,
  LearningOutcomesCard,
  initialsOf,
  OverviewSection,
  parseIdParam,
  performanceLabel,
  projectLabel,
  ReportLayout,
  StatTile,
  StudentProjectAnalysis,
  StudentProjectWorkTabs,
  TimeSpentCard,
  toPercent,
  uploadsToMedia,
  useCommonServiceClientContext,
  useGilReport,
} from '@geneo2-web/shared-ui';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimelineIcon from '@mui/icons-material/Timeline';
import { GilProjectStatusEnum } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GIL_Reports_Student } from '../../../routeHandling/RoutesNomenclature';

/** Dashboard 1 - one student's time, score and work on one project. */
export const GilStudentProjectReport = () => {
  const navigate = useNavigate();
  const { student_id, assessment_id } = useParams();
  const assessmentId = parseIdParam(assessment_id);
  const { LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();

  const { state, reload } = useGilReport(
    () =>
      student_id && /^\d+$/.test(student_id) && assessmentId
        ? LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.getGilStudentProjectReport(
            { studentId: BigInt(student_id), assessmentId }
          ).then((response) => response.data)
        : undefined,
    [student_id, assessmentId]
  );

  const report = state.status === 'success' ? state.data : undefined;
  //~ On a group project the group's shared lab device did the work, so the
  //~ group's time and coding are credited to the student; their own login may
  //~ show none of it.
  const groupWork = report?.groupWork;
  const timeSpent = groupWork?.timeSpent ?? report?.timeSpent;
  const score = report?.projectScore;
  const scorePercent = score?.isGraded
    ? toPercent(score.obtainedMarks, score.maxMarks)
    : undefined;
  const submitted =
    report?.status === GilProjectStatusEnum.GIL_PROJECT_STATUS_COMPLETED;
  const gallery = useMemo(
    () => uploadsToMedia(report?.userUploads),
    [report?.userUploads]
  );

  return (
    <ReportLayout
      avatarText={initialsOf(report?.studentName) || undefined}
      showImage
      title={report?.studentName || 'Student Report'}
      subtitle={report ? projectLabel(report.project) : undefined}
      meta={
        report && [
          {
            value: 'Student',
            iconName: 'user',
            color: '#EEF0FF',
            textColor: '#2E3192',
            bold: true,
          },
          {
            label: 'Class',
            value: formatClassSection(report.classSection),
            color: '#FFF8E6',
            textColor: GIL.text,
            bold: true,
          },
          {
            label: 'Group',
            value: report.groupName,
            color: '#FDEEF2',
            textColor: '#4B4F8A',
            bold: true,
          },
        ]
      }
      headerRight={
        //~ Not in the Figma yet: the way from one project to the student's
        //~ whole GIL journey.
        student_id && (
          <HeaderButton
            label="GIL Journey"
            icon={<TimelineIcon />}
            onClick={() => navigate(`${GIL_Reports_Student}/${student_id}`)}
          />
        )
      }
      state={state}
      onRetry={reload}
    >
      <DataStatusNote status={report?.dataStatus} />

      <OverviewSection subtitle="Quick summary of the student's activity and performance">
        <StatTile
          tone="green"
          icon="gil-group-green"
          value={1}
          label="Project Assigned"
        />
        <StatTile
          tone="blue"
          icon="gil-submitted-blue"
          value={`${submitted ? 1 : 0}/1`}
          label="Submitted"
        />
        <StatTile
          tone="yellow"
          icon={<AccessTimeIcon />}
          value={formatDuration(timeSpent?.totalTimeInSec)}
          label={groupWork ? 'Group Time Spent' : 'Time Spent'}
          caption="On this project"
        />
        <StatTile
          tone="red"
          icon="gil-star-red"
          value={formatPercent(scorePercent)}
          label="Project Score"
          caption={performanceLabel(scorePercent)}
        />
      </OverviewSection>

      <TimeSpentCard
        videoSec={timeSpent?.videoActivityBookTimeInSec}
        codingSec={timeSpent?.codingTimeInSec}
        subtitle={
          groupWork
            ? `${groupWork.groupName || 'Group'} - on the group's shared device`
            : 'Learning time across all activities of this project'
        }
        effortText={`${
          report?.studentName || 'The student'
        } is putting consistent effort into their learning journey.`}
      />

      <CodingActivityCard
        activity={groupWork?.codingActivity ?? report?.codingActivity}
        title={groupWork ? 'Group Coding Activity' : 'Coding Activity'}
        subtitle={
          groupWork
            ? "Everything done on the group's shared device"
            : undefined
        }
      />

      <StudentProjectAnalysis report={report} />

      <LearningOutcomesCard
        outcomes={report?.learningOutcomes}
        showMarks
        subtitle="How the student is doing on each learning outcome of this project"
      />

      <StudentProjectWorkTabs
        gallery={gallery}
        galleryTitle={report?.studentName}
        evidenceSubtitle="Photos and videos the student uploaded"
      />
    </ReportLayout>
  );
};

export default GilStudentProjectReport;
